import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { generateRandomString } from "utils/helper";
import * as services from "../service/notificationApi";
import { setIsLoading, setNotificationList } from "../slices/notificationSlice";

function useNotification() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isLoading = useSelector((state) => state.notification.isLoading);
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const currentEmployee = useSelector((state) => state.company.currentEmployee);
    const notificationList = useSelector((state) => state.notification.notificationList);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [templateOptions, setTemplateOptions] = useState([]);
    const [emailLoopOptions, setEmailLoopOptions] = useState([]);

    const getNotificationList = useCallback(async () => {
        try {
            dispatch(setIsLoading(true));
            const response = await services.getNotificationList({
                requestId: generateRandomString(),
                companyId: currentCompany,
            });
            dispatch(setNotificationList(response.data?.content || []));
        } catch (error) {
            message.error(t("common.error"));
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, dispatch, t]);

    useEffect(() => {
        if (typeof currentCompany !== "object" && currentCompany !== null) {
            getNotificationList();
        }
    }, [currentCompany]);

    const loadTemplateOptions = useCallback(async () => {
        const response = await services.getEmailTemplateList({
            requestId: generateRandomString(),
            companyId: currentCompany,
        });
        const list = response.data?.content || [];
        setTemplateOptions(
            list
                .filter((tpl) => tpl.statusFlag === "ACTIVE")
                .map((tpl) => ({
                    value: tpl.id,
                    label: `${tpl.templateName} (${tpl.isHr
                        ? t("setting_email_template_list.form.recipient_hr")
                        : t("setting_email_template_list.form.recipient_employee")
                        })`,
                    templateName: tpl.templateName,
                    emailTemplateCode: tpl.emailTemplateCode,
                    isHr: tpl.isHr,
                }))
        );
    }, [currentCompany]);
    console.log(templateOptions.map((o) => [o.value, o.templateName, o.emailTemplateCode]))
    const loadEmailLoopOptions = useCallback(
        async (record) => {
            const response = await services.getEmailLoopList({
                requestId: generateRandomString(),
                companyId: currentCompany,
            });
            const list = response.data?.content || [];

            const activeOptions = list
                .filter((g) => g.isActive === "Y")
                .map((g) => ({ value: g.id, label: g.groupName }));

            const existing = record
                ? [...(record.toList || []), ...(record.ccList || []), ...(record.bccList || [])]
                    .map((r) => ({ value: r.value, label: r.label }))
                : [];

            const map = new Map();
            [...existing, ...activeOptions].forEach((o) => map.set(o.value, o));
            setEmailLoopOptions(Array.from(map.values()));
        },
        [currentCompany]
    );

    const onCreate = useCallback(async () => {
        await Promise.all([loadTemplateOptions(), loadEmailLoopOptions(null)]);
        setEditingRecord(null);
        setDrawerOpen(true);
    }, [loadTemplateOptions, loadEmailLoopOptions]);

    const onEdit = useCallback(
        async (record) => {
            await Promise.all([loadTemplateOptions(), loadEmailLoopOptions(record)]);
            setEditingRecord(record);
            setDrawerOpen(true);
        },
        [loadTemplateOptions, loadEmailLoopOptions]
    );

    const onCloseDrawer = useCallback(() => {
        setDrawerOpen(false);
        setEditingRecord(null);
    }, []);

    const onSubmit = useCallback(
        async (values) => {
            try {
                dispatch(setIsLoading(true));
                await services.saveNotification({
                    id: editingRecord?.id,
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    actionId: currentEmployee,
                    templateId: editingRecord ? undefined : values.templateId,
                    toList: values.toList || [],
                    ccList: values.ccList || [],
                    bccList: values.bccList || [],
                    isEmail: values.isEmail || false,
                    isMobile: values.isMobile || false,
                    isActive: values.isActive ? "Y" : "N",
                    timingRuleList: (values.timingRuleList || []).map((r) => ({
                        value: r.condition === "ON" ? 0 : r.value,
                        unit: r.unit ?? "DAY",
                        condition: r.condition,
                    })),
                });
                message.success(t("common.success"));
                setDrawerOpen(false);
                setEditingRecord(null);
                getNotificationList();
            } catch (error) {
                message.error(t("common.error"));
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, currentEmployee, editingRecord, dispatch, getNotificationList, t]
    );

    const onStatus = useCallback(
        async (checked, record) => {
            try {
                dispatch(setIsLoading(true));
                await services.saveNotification({
                    id: record.id,
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    actionId: currentEmployee,
                    isActive: checked ? "Y" : "N",
                });
                message.success(t("common.success"));
                getNotificationList();
            } catch (error) {
                message.error(t("common.error"));
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, currentEmployee, dispatch, getNotificationList, t]
    );

    const onDelete = useCallback(
        (record) => {
            Modal.confirm({
                title: t("dialog.confirmation.header"),
                content: <p>{t("dialog.confirmation.delete").replace("{entity}", record.templateName)}</p>,
                async onOk() {
                    try {
                        dispatch(setIsLoading(true));
                        await services.deleteNotification({
                            id: record.id,
                            requestId: generateRandomString(),
                            companyId: currentCompany,
                            actionId: currentEmployee,
                        });
                        message.success(t("common.success"));
                        getNotificationList();
                    } catch (error) {
                        message.error(t("common.error"));
                    } finally {
                        dispatch(setIsLoading(false));
                    }
                },
                okText: t("common.confirm").toUpperCase(),
                cancelText: t("common.cancel").toUpperCase(),
            });
        },
        [currentCompany, currentEmployee, dispatch, getNotificationList, t]
    );

    return {
        notificationList,
        isLoading,
        drawerOpen,
        editingRecord,
        templateOptions,
        emailLoopOptions,
        onCreate,
        onEdit,
        onCloseDrawer,
        onSubmit,
        onStatus,
        onDelete,
    };
}

export default useNotification;